import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  Button,
  message,
  Col,
  Select,
  Spin,
  Row,
} from 'antd';
import { UploadFile } from 'antd/lib/upload';
import {
  UploadOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { updateJobApplication, getSingleJobApplication, deleteJobDocumentImage, getStatus, createStatus, updateStatus, deleteStatus, getAllSources } from '../../utils/API';
import { updateJobApplicationStatus } from '../../utils/API';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import dayjs from 'dayjs';
import { FaPen, FaTrashAlt } from 'react-icons/fa';
import { ColorPicker } from 'antd';

interface JobApplicationFormValues {
  _id: string;
  key: string;
  full_name: string;
  date: moment.Moment;
  email: string;
  city: string;
  phone_number: string;
  technology: string;
  total_experience: number;
  relevant_experience: number;
  current_ctc: number;
  expected_ctc: number;
  resume?: string;
  source: string;
  status: string;
  jobDesc: string;
  remark?: string;
}

interface EditJobApplicationModalProps {
  customerId?: any;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Status {
  _id: string;
  name: string;
  color?: string;
}

const EditJobApplicationModal: React.FC<EditJobApplicationModalProps> = ({
  visible,
  onClose,
  onSuccess,
  customerId,
}) => {
  const [form] = Form.useForm();
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + '/job-application/';
  const [updating, setUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [resumes, setResumes] = useState<Array<string | RcFile>>([]);
  const [otherSource, setOtherSource] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>();
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState<string>('#000000');
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sources, setSources] = useState<Array<string>>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [position, setPosition] = useState<string | any>();




  const technologyOptions: Record<string, string[]> = {
    "Frontend Developer": [
      // 🌐 Core
      "HTML5",
      "CSS3",
      "JavaScript (ES6+)",
      "TypeScript",

      // ⚛️ Frameworks & Libraries
      "React",
      "Next.js",
      "Angular",
      "Vue.js",
      "Nuxt.js",
      "Svelte",
      "SvelteKit",

      // 🎨 Styling & UI
      "Tailwind CSS",
      "Bootstrap",
      "Material UI",
      "Chakra UI",
      "Ant Design",

      // 📦 State Management
      "Redux",
      "Zustand",
      "Recoil",
      "Pinia",

      // ⚡ Build Tools
      "Vite",
      "Webpack",

      // 🔧 Testing
      "Jest",
      "React Testing Library",
      "Cypress",
      "Playwright",

      // 📱 Cross-Platform
      "React Native",
      "Flutter (Web Support)",

      // 📐 Static Site / Modern Meta Frameworks
      "Astro",
      "Gatsby",

      // 🛠 Other
      "GraphQL (Apollo)",
      "Storybook",
      "ESLint",
      "Prettier"
    ],
    "Backend Developer": [
      // ⚙️ Languages
      "JavaScript (Node.js)",
      "TypeScript",
      "Python",
      "Java",
      "C#",
      "PHP",
      "Go",
      "Ruby",
      "Rust",
      "Kotlin",
      "Scala",

      // 🖥 Frameworks & Runtimes
      "Node.js",
      "Express.js",
      "NestJS",
      "Django",
      "Flask",
      "FastAPI",
      "Spring Boot",
      "Micronaut",
      "Quarkus",
      "Laravel",
      "Symfony",
      "CodeIgniter",
      "Ruby on Rails",
      "Phoenix (Elixir)",
      "Fiber (Go)",
      "Gin (Go)",
      "Actix (Rust)",
      "Rocket (Rust)",

      // 🗄 Databases
      "MySQL",
      "PostgreSQL",
      "SQLite",
      "MongoDB",
      "Redis",
      "MariaDB",
      "Oracle DB",
      "MS SQL Server",
      "Cassandra",
      "CouchDB",
      "Firebase Realtime DB",
      "DynamoDB",

      // 📡 APIs & Communication
      "REST API",
      "GraphQL",
      "gRPC",
      "WebSockets",

      // ☁️ Cloud & Serverless
      "AWS Lambda",
      "Google Cloud Functions",
      "Azure Functions",
      "Serverless Framework",

      // 🐳 DevOps Tools
      "Docker",
      "Kubernetes",

      // 🧪 Testing
      "JUnit",
      "PyTest",
      "Mocha",
      "Jest",
      "Supertest",
      "Postman/Newman"
    ],
    "Fullstack Developer": [
      // 🌐 JavaScript/TypeScript Stacks
      "MERN (MongoDB, Express.js, React, Node.js)",
      "MEAN (MongoDB, Express.js, Angular, Node.js)",
      "MEVN (MongoDB, Express.js, Vue.js, Node.js)",
      "Next.js + Node.js",
      "NestJS + React",
      "RedwoodJS (Fullstack React/GraphQL framework)",
      "Blitz.js (Next.js based fullstack framework)",

      // 🐘 Python Based
      "Django + React",
      "Django + Angular",
      "Flask + React",
      "FastAPI + Vue",

      // ☕ Java Based
      "Spring Boot + React",
      "Spring Boot + Angular",

      // 🐘 PHP Based
      "LAMP (Linux, Apache, MySQL, PHP)",
      "Laravel + Vue.js",
      "Laravel + React",
      "Symfony + React",

      // 🚀 Other Popular Stacks
      "Ruby on Rails + React",
      "Ruby on Rails + Hotwire",
      "Elixir Phoenix + LiveView",
      "Go + React",
      "Go + Vue",
      "ASP.NET Core + React",
      "ASP.NET Core + Angular",

      // ☁️ Modern Fullstack/Meta Frameworks
      "Remix (React Fullstack Framework)",
      "Nuxt.js (Vue Fullstack Framework)",
      "SvelteKit (Svelte Fullstack Framework)",
      "Astro (with React/Vue/Svelte integrations)"
    ],
    "App Developer": [
      // Native Platforms
      "Android (Kotlin, Java)",
      "iOS (Swift, Objective-C)",
      // Mobile App Frameworks
      "React Native",
      "Flutter",
      "Ionic",
      "Xamarin",
    ]
    // "UI/UX Designer": ["Figma", "Adobe XD", "Sketch", "InVision"],
    // "Business Development Executive": ["Lead Generation", "Client Communication", "Salesforce"],
  };




  const fetchSource = async () => {
    try {
      const getSource = await getAllSources(searchText, page);
      if (getSource) {
        let filteredSource = getSource.sources.filter((item: any) =>
          item.category.includes('JobApplication')
        );
        setSources(filteredSource);
      }

    } catch (error) {
      console.error('Error fetching source:', error);
    }
  }


  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await getStatus();
        if (response?.data) {
          setStatuses(response.data);
        }
      } catch (error) {
        console.error('Error fetching statuses:', error);
        message.error('Failed to load statuses');
      }
    };


    fetchSource()
    fetchStatuses();
  }, []);

  const handleAddNewStatus = async () => {
    if (!newStatusName.trim()) {
      message.warning('Please enter a status name');
      return;
    }

    try {
      if (editingStatus) {
        await updateStatus(editingStatus._id, { name: newStatusName, color: newStatusColor });
        message.success('Status updated successfully');
        setEditingStatus(null);
      } else {
        await createStatus({ name: newStatusName, color: newStatusColor });
        message.success('Status added successfully');
      }

      const res = await getStatus();
      setStatuses(res.data);
      setNewStatusName('');
      setNewStatusColor('#000000');
      setIsAddingStatus(false);

    } catch (error) {
      console.error('Error handling status:', error);
      message.error(editingStatus ? 'Failed to update status' : 'Failed to add new status');
    }
  };

  const handleEditStatus = (status: Status) => {
    setIsAddingStatus(true);
    setNewStatusName(status.name);
    setNewStatusColor(status.color || '#000000');
    setEditingStatus(status);
    setIsStatusDropdownOpen(true);
  };

  const handleDeleteStatus = async (statusId: string) => {
    try {
      await deleteStatus(statusId, {});
      const res = await getStatus();
      setStatuses(res.data);
      message.success('Status deleted successfully');

      if (statuses.find(s => s._id === statusId)?.name === selectedStatus) {
        setSelectedStatus('Pending');
      }
    } catch (error) {
      console.error('Error deleting status:', error);
      message.error('Failed to delete status');
    }
  };

  useEffect(() => {
    return () => {
      fileList.forEach((file) => {
        if (file.preview && typeof file.preview === 'string' && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
      setResumes([]);
      setFileList([]);
    };
  }, []);

  const handleDeleteDocument = async (doc: string | File, index: number) => {
    try {
      if (typeof doc === 'string') {
        const documentName = doc.split('/').pop() || '';
        await deleteJobDocumentImage(customerId, documentName);
        message.success('Document deleted successfully');
      }
      setResumes(resumes.filter((_, i) => i !== index));
      setFileList(fileList.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting document:', error);
      message.error('Failed to delete document');
    }
  };

  useEffect(() => {
    if (!visible || !customerId) {
      setResumes([]);
      setFileList([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setResumes([]);
        setFileList([]);

        const response = await getSingleJobApplication(customerId);
        if (response?.data) {
          const fetchedTech = response.data.technology;

          // ✅ Infer position from technology
          let inferredPosition: string | undefined = undefined;
          for (const [pos, techs] of Object.entries(technologyOptions)) {
            if (techs.includes(fetchedTech)) {
              inferredPosition = pos;
              break;
            }
          }
          setPosition(inferredPosition);

          const formattedData = {
            ...response.data,
            date: response.data.date ? moment(response.data.date) : moment(),
            status: response.data.status?._id,
            source: response.data.source?._id,
            position: inferredPosition,
            technology: fetchedTech,
          };

          // ✅ Set inferred position
          setPosition(inferredPosition);

          // ✅ Set form values
          form.setFieldsValue({
            ...formattedData,
            // set technology explicitly
            technology: fetchedTech,
          });

          setSelectedStatus(response.data.status?.name);

          if (response.data.resume) {
            const resumes = Array.isArray(response.data.resume)
              ? response.data.resume
              : [response.data.resume];

            setResumes(resumes);

            setFileList(
              resumes.map((resume: string, index: number) => ({
                uid: `existing-${index}-${Date.now()}`,
                name: resume.split('/').pop() || 'resume',
                status: 'done',
                url: `${IMAGEURL}${resume}`,
                ...(resume.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                  ? {
                    preview: `${IMAGEURL}${resume}`,
                  }
                  : {}),
              }))
            );
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        message.error('Failed to load application data');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    return () => {
      form.resetFields();
      setResumes([]);
      setFileList([]);
    };
  }, [visible, customerId, form, IMAGEURL]);


  const handleSubmit = async (values: JobApplicationFormValues) => {
    setUpdating(true);
    try {
      const formData = new FormData();
      const sourceToSubmit = values.source === 'Other' ? otherSource : values.source;

      const finalValues = {
        ...values,
      };

      Object.entries(finalValues).forEach(([key, value]) => {
        if (key === 'date' && value && typeof value === 'object' && 'format' in value) {
          formData.append(key, value.format('YYYY-MM-DD'));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('resume', fileList[0].originFileObj);
      }

      const resumeToKeep: string[] = [];

      resumes.forEach((doc: any) => {
        if (doc instanceof File) {
          formData.append(`resume`, doc, doc.name);
        } else if (typeof doc === 'string') {
          resumeToKeep.push(doc);
        }
      });

      formData.append('resumeToKeep', JSON.stringify(resumeToKeep));

      await updateJobApplication(customerId, formData);
      // const statusData = { status: selectedStatus };
      // await updateJobApplicationStatus(customerId, statusData);

      message.success('Application and status updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      message.error('Failed to update application or status');
    } finally {
      setUpdating(false);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    if (ext === "pdf") return <FilePdfOutlined className="text-2xl text-red-500 mb-1" />;
    if (ext === "doc" || ext === "docx") return <FileWordOutlined className="text-2xl text-blue-500 mb-1" />;
    if (ext === "jpg" || ext === "jpeg" || ext === "png")
      return <FileImageOutlined className="text-2xl text-green-500 mb-1" />;

    // default fallback
    return <FileUnknownOutlined className="text-2xl text-gray-500 mb-1" />;
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered={false}
      destroyOnClose
      className="custom-modal"
      style={{
        bottom: 20,
        top: 20,
        // maxHeight: 'calc(100vh - 30px)',
        margin: '0 auto',
        position: 'relative',
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit Job Application
        </div>
      }
    >
      <Spin spinning={loading}>
        <Form
          onFinish={handleSubmit}
          form={form}
          layout="horizontal"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          colon={false}
          style={{ margin: 0 }}
        >
          <div className="elements">
            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Full Name <span className="text-danger">*</span></span>}
              name="full_name"
              required={false}
              rules={[{ required: true, message: 'Please enter your full name' }]}
            >
              <Input placeholder="Fullname" className="Allinput" />
            </Form.Item>

            <Form.Item
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Date <span className="text-danger">*</span>
                </span>
              }
              name="date"
              required={false}
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                className="Allinput"
                format="DD-MM-YYYY"
                style={{ width: "100%" }}
                value={form.getFieldValue('date') || dayjs()}
                defaultPickerValue={dayjs()}
                disabledDate={current => current && current.isAfter(dayjs().endOf("day"))}
                onChange={(date) => form.setFieldsValue({ date })}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Email <span className="text-danger">*</span></span>}
              name="email"
              required={false}
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="Email" className="Allinput" />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Contact <span className="text-danger">*</span></span>}
              name="phone_number"
              required={false}
              rules={[
                { required: true, message: 'Please enter your phone number' },
                { pattern: /^[0-9+\- ]+$/, message: 'Please enter a valid phone number' },
              ]}
            >
              <Input placeholder="Contact" className="Allinput" />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>City <span className="text-danger">*</span></span>}
              name="city"
              required={false}
              rules={[{ required: true, message: 'Please enter your city' }]}
            >
              <Input placeholder="city" className="Allinput" />
            </Form.Item>

            <Form.Item
              name="position"
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Position <span className="text-red-500">*</span>
                </span>
              }
              required={false}

              rules={[{ required: true, message: "Please select the position" }]}
            >
              <Select
                placeholder="Select Position"
                className="CustomSelect"
                value={position}
                onChange={(value) => {
                  setPosition(value);
                  form.setFieldsValue({ technology: undefined });
                }}
              >
                <Select.Option value="Frontend Developer">Frontend Developer</Select.Option>
                <Select.Option value="Backend Developer">Backend Developer</Select.Option>
                <Select.Option value="Fullstack Developer">Fullstack Developer</Select.Option>
                <Select.Option value="App Developer">App Developer</Select.Option>
                <Select.Option value="UI/UX Designer">
                  UI/UX Designer
                </Select.Option>
                <Select.Option value="Business Development Executive">
                  Business Development Executive
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Relevant Technology
                </span>
              }
              name="technology"
            >
              <Select
                placeholder="Select Technology"
                className="CustomSelect"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {technologyOptions[position]?.map((tech: any) => (
                  <Select.Option key={tech} value={tech}>
                    {tech}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Source <span className="text-danger">*</span></span>}
              name="source"
              required={false}
              rules={[{ required: true, message: 'Please select the source' }]}
            >
              <Select
                placeholder="Select Source"
                className="CustomSelect"
              // onChange={(value) => form.setFieldsValue({ source: value._id })}
              >
                {sources.map((source: any) => (
                  <Select.Option value={source._id}>{source.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Total Experience <span className="text-danger">*</span></span>}
              name="total_experience"
              required={false}
              rules={[{ required: true, message: 'Please enter total experience' }]}
            >
              <Input className="Allinput" placeholder="Total experience" />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Relevant Experience<span className="text-danger">*</span></span>}
              name="relevant_experience"
              required={false}
              rules={[{ required: true, message: 'Please enter relevant experience' }]}
            >
              <Input className="Allinput" placeholder="Relevant experience" />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Current CTC <span className="text-danger">*</span></span>}
              name="current_ctc"
              required={false}
              rules={[{ required: true, message: 'Please enter current CTC' }]}
            >
              <Input className="Allinput" placeholder="current experience" />
            </Form.Item>

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Expected CTC <span className="text-danger">*</span></span>}
              name="expected_ctc"
              required={false}
              rules={[{ required: true, message: 'Please enter expected CTC' }]}
            >
              <Input className="Allinput" placeholder="expected experience" />
            </Form.Item>

            <Form.Item
              label={
                <span
                  style={{
                    color: '#4b5563',
                    fontFamily: 'inter, sans-serif',
                    fontSize: '14px',
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Status <span className="text-danger">*</span>
                </span>
              }
              name="status"
              rules={[{ required: true, message: 'Please select the Status' }]}
              required={false}
            >
              <Select
                value={selectedStatus}
                onChange={(value) => {
                  setSelectedStatus(value);
                  form.setFieldsValue({ status: value });
                }}
                style={{ width: '100%' }}
                open={isStatusDropdownOpen}
                onDropdownVisibleChange={(open) => setIsStatusDropdownOpen(open)}
                dropdownRender={() => (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {/* Status list with scroll */}
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {statuses.map((status) => (
                        <div
                          key={status._id}
                          className="status-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px 12px',
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedStatus(status._id);           // update local state
                            form.setFieldsValue({ status: status._id }); // update form
                            setIsStatusDropdownOpen(false);
                          }}
                        >
                          <span style={{ flex: 1, color: status.color || '#000000' }}>
                            {status.name
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                          <FaPen
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditStatus(status);
                            }}
                            className="group-hover:scale-110 text-indigo-500 transition-transform duration-200 mr-2"
                            size={12}
                          />
                          <FaTrashAlt
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteStatus(status._id);
                            }}
                            className="group-hover:scale-110 text-red-500 transition-transform"
                            size={12}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Fixed Add/Edit controls */}
                    <div
                      style={{
                        position: 'sticky',
                        bottom: 0,
                        background: '#fff',
                        padding: '8px',
                        borderTop: '1px solid #f0f0f0',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {isAddingStatus ? (
                        <>
                          <div className="flex">
                            <Input
                              placeholder={editingStatus ? "Edit status name" : "Enter new status name"}
                              value={newStatusName}
                              onChange={(e) => setNewStatusName(e.target.value)}
                              style={{ marginBottom: '8px', width: '90%', height: '35px', borderColor: "#d9d9d9", borderRadius: "5px" }}
                            />
                            <ColorPicker
                              size="small"
                              value={newStatusColor}
                              onChange={(color) => setNewStatusColor(color.toHexString())}
                              onChangeComplete={(color) => setNewStatusColor(color.toHexString())}
                              getPopupContainer={(trigger: any) => trigger.parentElement}
                              style={{ margin: 'auto', padding: "5px" }}
                            />
                          </div>
                          <div className="flex w-full justify-end gap-2">
                            <Button
                              size="small"
                              onClick={() => {
                                setIsAddingStatus(false);
                                setNewStatusName('');
                                setNewStatusColor('#000000');
                                setEditingStatus(null);
                              }}
                              style={{ width: "140px", height: "35px", borderRadius: '4px' }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              onClick={handleAddNewStatus}
                              style={{ width: "140px", height: "35px", backgroundColor: "#001529", color: "#ffffff" }}
                            >
                              {editingStatus ? 'Update' : 'Save'}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div style={{ padding: '4px', width: '100%', display: 'flex', justifyContent: "end" }}>
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => setIsAddingStatus(true)}
                            style={{ backgroundColor: "#001529", color: "#ffffff", border: 'none' }}
                          >
                            Add New Status
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              >
                {statuses.map((status) => (
                  <Select.Option key={status._id} value={status._id}>
                    {status.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>


            {/* <Form.Item
              label={
                <span style={{
                  color: '#4b5563',
                  fontFamily: 'inter, sans-serif',
                  fontSize: '14px',
                  paddingLeft: "15px",
                  fontWeight: 500,
                }}>
                  Remark
                </span>
              }
              name="remark"
              required={false}
              rules={[{ required: false, message: 'Enter remark' }]}
            >
              <Input
                placeholder="Enter remark"
                className="Allinput"
              />
            </Form.Item> */}

            <Form.Item
              label={<span style={{
                color: '#4b5563',
                fontFamily: 'inter, sans-serif',
                fontSize: '14px',
                paddingLeft: "15px",
                fontWeight: 500,
              }}>Description <span className="text-red-500">*</span></span>}
              name="jobDesc"
              required={false}
              rules={[{ required: true, message: 'Enter Job description' }]}
            >
              <Input.TextArea placeholder="Enter Job description" className="Allinputtextarea" />
            </Form.Item>

            <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
              <Row className="bg-white">

                <Col span={24}>
                  <Form.Item
                    label={<span style={{
                      color: '#4b5563',
                      fontFamily: 'inter, sans-serif',
                      fontSize: '14px',
                      paddingLeft: "15px",
                      fontWeight: 500,
                    }}>Resume</span>}
                    name="resume"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e.fileList}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <Upload
                          action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                          listType="picture-card"
                          maxCount={1}
                          showUploadList={false}
                          onChange={(info: UploadChangeParam<UploadFile<File>>) => {
                            if (info.fileList.length > 0 && resumes.length === 0) {
                              const newFile = info.fileList[0].originFileObj;
                              if (newFile) {
                                setResumes([newFile]);
                                setFileList([{
                                  uid: `new-${Date.now()}`,
                                  name: newFile.name,
                                  status: 'done',
                                  url: URL.createObjectURL(newFile),
                                }]);
                              }
                            }
                          }}
                          beforeUpload={() => false}
                          disabled={resumes.length > 0}
                          openFileDialogOnClick={resumes.length === 0}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            disabled={resumes.length > 0}
                          >
                            Upload
                          </Button>
                        </Upload>
                      </div>

                      {resumes.map((doc, index) => {
                        if (!doc) return null;

                        const fileName = typeof doc === 'string'
                          ? doc.split('/').pop() || ''
                          : (doc as RcFile).name;
                        const fileUrl = typeof doc === 'string'
                          ? `${IMAGEURL}${doc}`
                          : URL.createObjectURL(doc as RcFile);

                        return (
                          <div
                            key={index}
                            className="w-38 h-32 border-2 border-gray-200 rounded-lg bg-white relative hover:shadow-md transition-all flex flex-col items-center justify-center p-4"
                          >
                            {getFileIcon(fileName)} {/* ✅ dynamic icon */}
                            <span className="text-xs font-medium text-gray-700 truncate w-full text-center">
                              {fileName}
                            </span>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute inset-0"
                              onClick={(e) => e.stopPropagation()}
                            ></a>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteDocument(doc, index);
                              }}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 cursor-pointer bg-white rounded-full p-1 flex items-center justify-center"
                              style={{ width: '22px', height: '22px' }}
                            >
                              <DeleteOutlined style={{ fontSize: '12px' }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </div>
          <Row justify="end">
            <Col xs={24} sm={24} md={24} xl={24} xxl={20} className="d-flex justify-content-end me-3">
              <div className="button-wrapper">
                <button
                  className="allcanclebutton"
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="allsubmitebutton w-26"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </Col>
          </Row>
        </Form >
      </Spin >
    </Modal >
  );
};

export default EditJobApplicationModal;